import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { StylesContainerVariables } from 'chaoskit/src/components/Container'
import { Button } from 'chaoskit/src/components'
import useInterval from 'react-use/lib/useInterval'

import companyQuotes from './customer-quotes.json'

import Data from '~components/Data'
import Quote from '~components/Quote'
import ButtonLink from '~components/ButtonLink'
import gridPattern from '~media/home/customer-grid-pattern.svg'
import Image from '~components/Image'
import Icon from '~components/Icon'

const StylesQuoteGrid = {
  content: "''",
  width: 250,
  height: 450,
  background: `url(${gridPattern}) no-repeat center center`,
  backgroundSize: 'cover',
  position: 'absolute',
  right: 0,
  transform: 'translateX(50%)',
  zIndex: 0,
}

const CustomerQuotePanel = ({ customer, hideCta, activeIndex, index }) => {
  const theme = useTheme()

  return (
    <div
      aria-hidden={activeIndex !== index}
      css={{
        maxWidth: StylesContainerVariables.extended,
        margin: '0 auto',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        css={{
          width: '100%',
          overflow: 'hidden',

          '&::after': {
            ...StylesQuoteGrid,
            bottom: theme.space.large,
          },

          [theme.mq.medium]: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            alignContent: 'center',

            '&::after': {
              display: 'none',
            },
          },
        }}
      >
        <div
          css={{
            filter: 'grayscale(100%)',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center center',
            backgroundSize: 'cover',
            position: 'relative',
            zIndex: 1,

            [theme.mq.small__max]: {
              paddingBottom: 'calc(100% / (16 / 9))',
            },

            [theme.mq.medium]: {
              maskImage: `linear-gradient(
                    0deg,
                    transparent,
                    #fff 30%,
                    #fff 100%,
                    transparent
                  )`,
            },
          }}
        >
          <Image
            src={customer.image}
            css={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            alt={customer.name}
          />
        </div>
        <div
          css={{
            padding: theme.space.medium,
            paddingBottom: theme.space.xlarge,

            [theme.mq.medium]: {
              padding: theme.space.xlarge,
            },
          }}
        >
          <Quote
            author={customer.name}
            title={customer.position}
            company={customer.company}
            logo={customer.logo}
            size="large"
            contrastQuotes
          >
            <Data>{customer.quote}</Data>
          </Quote>

          {!hideCta && (
            <div
              css={{
                marginTop: theme.space.large,

                [theme.mq.medium]: {
                  marginTop: theme.space.xlarge,
                },
              }}
            >
              <ButtonLink noContrast url="/customers/" type="primary">
                Read Case Studies
              </ButtonLink>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

CustomerQuotePanel.propTypes = {
  activeIndex: PropTypes.number.isRequired,
  customer: PropTypes.object.isRequired,
  hideCta: PropTypes.any,
  index: PropTypes.number.isRequired,
}

const CustomerQuote = ({ hideCta }) => {
  const theme = useTheme()

  const [manualInteraction, setManualInteraction] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [delay, setDelay] = useState(4000)

  const previousSlide =
    activeIndex - 1 < 0 ? companyQuotes.quotes.length - 1 : activeIndex - 1
  const nextSlide =
    activeIndex + 1 > companyQuotes.quotes.length - 1 ? 0 : activeIndex + 1

  useInterval(() => {
    setActiveIndex(nextSlide)
  }, delay)

  useEffect(() => {
    // Stop automatically advancing slides when buttons are manually interacted with
    if (manualInteraction) {
      setDelay(null)
    }
  }, [manualInteraction])

  return (
    <section
      css={{
        background: theme.color.dark.base,
        position: 'relative',
        overflow: 'hidden',

        '&::after': {
          ...StylesQuoteGrid,
          top: theme.space.large,
          display: 'none',

          [theme.mq.medium]: {
            display: 'block',
          },
        },
      }}
      className="u-contrast"
    >
      <div
        css={{
          display: 'grid',
          gridAutoColumns: '100%',
          alignItems: 'start',
          gridAutoFlow: 'column',
          transition: `transform ${theme.timing.long} ${theme.transition.base}`,
        }}
        style={{ transform: `translateX(-${activeIndex * 100}%)` }}
        tabIndex="-1"
      >
        {companyQuotes.quotes.map((quote, index) => (
          <CustomerQuotePanel
            key={quote.company}
            customer={quote}
            hideCta={hideCta}
            activeIndex={activeIndex}
            index={index}
          />
        ))}
      </div>
      <div
        css={{
          position: 'absolute',
          bottom: theme.space.small,
          zIndex: 1,
          display: 'grid',
          gridAutoColumns: 'auto',
          gridAutoFlow: 'column',
          justifyContent: 'center',
          gap: theme.space.base,
          width: '100%',
        }}
      >
        <Button
          onClick={() => {
            setManualInteraction(true)
            setActiveIndex(previousSlide)
          }}
          title="Previous"
          iconOnly
          size="small"
        >
          <Icon icon="caret-left" />
        </Button>
        <Button
          onClick={() => {
            setManualInteraction(true)
            setActiveIndex(nextSlide)
          }}
          title="Next"
          iconOnly
          size="small"
        >
          <Icon icon="caret-right" />
        </Button>
      </div>
    </section>
  )
}

CustomerQuote.propTypes = {
  hideCta: PropTypes.bool,
}

export default CustomerQuote
